import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, bg, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Clock, Loader2, CheckCircle2, XCircle, Trophy, RotateCcw, Award, Send, ListChecks, Target, Timer, PlayCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import { HelpVideo } from "@/components/HelpVideo";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/** Prova final do aluno: instruções → questões (com cronômetro e salvamento automático) → resultado. */
export default function ExamPage({ preview = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const back = preview ? `/admin/cursos/${id}#prova` : `/app/curso/${id}`;
  const [st, setSt] = useState(null);
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const sentRef = useRef(false);

  const loadStatus = useCallback(() => api.get(`/courses/${id}/exam/status`).then((r) => setSt(r.data)).catch((e) => { toast.error(errMsg(e)); navigate(back); }), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/courses/${id}/exam/start`);
      setExam(r.data); setAnswers(r.data.answers || {}); setResult(null); sentRef.current = false;
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const submit = useCallback(async (auto = false) => {
    if (!exam || sentRef.current) return;
    if (!auto) {
      const blank = exam.questions.filter((q) => !(answers[q.id] || []).length).length;
      if (!(await confirmar(blank ? `Você deixou ${blank} questão(ões) sem resposta. Enviar mesmo assim?` : "Enviar a prova? Depois de enviada não dá para alterar."))) return;
    }
    sentRef.current = true; setBusy(true);
    try {
      const r = await api.post(`/courses/${id}/exam/attempts/${exam.attempt_id}`, { answers, final: true });
      setResult(r.data); setSt(r.data.status); setExam(null);
      if (auto) toast.info("Tempo esgotado — a prova foi enviada automaticamente.");
    } catch (e) { sentRef.current = false; toast.error(errMsg(e)); } finally { setBusy(false); }
  }, [exam, answers, id]);

  // cronômetro
  useEffect(() => {
    if (!exam?.deadline) { setLeft(null); return; }
    const tick = () => {
      const s = Math.max(0, (new Date(exam.deadline).getTime() - Date.now()) / 1000);
      setLeft(s);
      if (s <= 0) submit(true);
    };
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [exam, submit]);

  // salva as respostas a cada 20 s (se a internet cair, não perde o que já marcou)
  useEffect(() => {
    if (!exam) return;
    const t = setInterval(() => { api.post(`/courses/${id}/exam/attempts/${exam.attempt_id}`, { answers, final: false }, bg).catch(() => {}); }, 20000);
    return () => clearInterval(t);
  }, [exam, answers, id]);

  const pick = (q, oid) => setAnswers((p) => {
    if (q.type === "multipla") { const cur = p[q.id] || []; return { ...p, [q.id]: cur.includes(oid) ? cur.filter((x) => x !== oid) : [...cur, oid] }; }
    return { ...p, [q.id]: [oid] };
  });

  if (!st) return <div className="grid place-items-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;

  return (
    <main className="max-w-3xl mx-auto w-full px-4 md:px-6 py-6 space-y-5">
      <button onClick={() => navigate(back)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Voltar ao curso</button>

      {/* Resultado */}
      {result && (
        <div className={`rounded-2xl border p-6 text-center ${result.passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`} data-testid="exam-result">
          {result.passed ? <Trophy className="h-12 w-12 mx-auto text-[#1E9E2A]" /> : <XCircle className="h-12 w-12 mx-auto text-red-500" />}
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{result.score}%</div>
          <div className="text-slate-600">{result.right} de {result.total} acertos · nota mínima {result.pass_score}%</div>
          <div className={`mt-2 font-semibold ${result.passed ? "text-green-700" : "text-red-700"}`}>{result.passed ? "Aprovado(a)! 🎉" : "Não foi dessa vez."}{result.timed_out ? " (tempo esgotado)" : ""}</div>
          <div className="flex justify-center gap-2 mt-4 flex-wrap">
            {result.passed && !preview && <Button onClick={() => navigate(`/app/certificado/${id}`)} className="text-white" style={{ background: "#1E9E2A" }}><Award className="h-4 w-4 mr-1.5" /> Emitir certificado</Button>}
            {!result.passed && st.can_start && <Button onClick={start} variant="outline"><RotateCcw className="h-4 w-4 mr-1.5" /> Tentar novamente</Button>}
          </div>
          {!result.passed && !st.can_start && st.reason && <p className="text-sm text-slate-500 mt-3">{st.reason}</p>}
        </div>
      )}
      {result?.review?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <h2 className="font-display text-lg font-semibold">Gabarito</h2>
          {result.review.map((r, i) => (
            <div key={i} className="border-t border-slate-100 pt-3">
              <div className="flex gap-2 font-medium text-slate-800">{r.correct ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" /> : <XCircle className="h-5 w-5 text-red-500 shrink-0" />}<span>{i + 1}. {r.statement}</span></div>
              <ul className="mt-2 ml-7 space-y-1 text-sm">
                {r.options.map((o, j) => (
                  <li key={j} className={o.correct ? "text-green-700 font-medium" : o.chosen ? "text-red-600 line-through" : "text-slate-500"}>{o.correct ? "✓ " : o.chosen ? "✗ " : "• "}{o.text}</li>
                ))}
              </ul>
              {r.explanation && <p className="ml-7 mt-1 text-xs text-slate-500 bg-slate-50 rounded p-2">{r.explanation}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Instruções */}
      {!exam && !result && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="exam-intro">
          <h1 className="font-display text-2xl font-bold text-slate-900">{st.title}</h1>
          <HelpVideo moment="prova" label="Ver vídeo: como funciona a prova" title="Como funciona a prova final" />
          {st.instructions && <p className="text-slate-600 whitespace-pre-line">{st.instructions}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
            {[[ListChecks, "Questões", st.num_questions],
              [Target, "Nota mínima", `${st.pass_score}%`],
              [Timer, "Tempo", st.time_limit ? `${st.time_limit} min` : "Sem limite"],
              [RotateCcw, "Tentativas", st.max_attempts ? `${st.attempts_used} de ${st.max_attempts}` : "Ilimitadas"]].map(([Ic, rot, val]) => (
              <div key={rot} className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><Ic className="h-4 w-4 text-[#1E9E2A]" /> {rot}</div>
                <div className="mt-1 font-display text-xl font-bold text-[#1A2340]">{val}</div>
              </div>
            ))}
          </div>
          {st.best_score !== null && <p className="text-sm text-slate-500">Sua melhor nota até agora: <strong>{st.best_score}%</strong>{st.passed ? " — aprovado(a)" : ""}</p>}
          {st.reason && !st.in_progress && (() => {
            // "Conclua todas as aulas para liberar a prova (12/76)." -> barra de progresso + atalho para as aulas
            const m = /\((\d+)\/(\d+)\)/.exec(st.reason || "");
            const pct = m ? Math.round((100 * Number(m[1])) / Math.max(1, Number(m[2]))) : null;
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-800">
                <div className="flex items-start gap-2"><Lock className="h-4 w-4 mt-0.5 shrink-0" /><span>{st.reason}{st.next_available_at ? ` Liberada em ${new Date(st.next_available_at).toLocaleString("pt-BR")}.` : ""}</span></div>
                {pct !== null && (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="h-2 flex-1 min-w-[140px] rounded-full bg-amber-200/70 overflow-hidden"><div className="h-full rounded-full bg-[#1E9E2A]" style={{ width: `${pct}%` }} /></div>
                    <span className="text-xs font-bold">{pct}%</span>
                    {!preview && <Button onClick={() => navigate(`/app/curso/${id}`)} className="h-9 text-white w-full sm:w-auto" style={{ background: "#1E9E2A" }}><PlayCircle className="h-4 w-4 mr-1.5" /> Ir para as aulas</Button>}
                  </div>
                )}
              </div>
            );
          })()}
          <div className="flex flex-col sm:flex-row gap-2">
            {st.passed && !preview && <Button onClick={() => navigate(`/app/certificado/${id}`)} className="text-white" style={{ background: "#1E9E2A" }}><Award className="h-4 w-4 mr-1.5" /> Emitir certificado</Button>}
            {st.can_start && (
              <Button onClick={start} disabled={busy} className="h-12 text-base text-white px-10 w-full sm:w-auto" style={{ background: "#232D4F" }} data-testid="exam-start">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : st.in_progress ? "Continuar a prova" : "Iniciar a prova"}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Prova */}
      {exam && (
        <>
          <div className="sticky top-16 z-20 bg-white/95 backdrop-blur border border-slate-200 rounded-xl px-4 pt-3 pb-2.5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="font-semibold text-slate-800 truncate">{exam.title}</div>
              <div className="flex items-center gap-4 text-sm shrink-0">
                <span className="text-slate-500"><strong className="text-[#1A2340]">{Object.values(answers).filter((v) => v?.length).length}</strong>/{exam.questions.length} respondidas</span>
                {left !== null && <span className={`flex items-center gap-1 font-mono font-bold ${left < 60 ? "text-red-600" : "text-[#232D4F]"}`} data-testid="exam-timer"><Clock className="h-4 w-4" /> {mmss(left)}</span>}
              </div>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#1E9E2A] to-[#C9A24A] transition-all" style={{ width: `${(100 * Object.values(answers).filter((v) => v?.length).length) / Math.max(1, exam.questions.length)}%` }} /></div>
          </div>
          {exam.questions.map((q, i) => (
            <div key={q.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`exam-q-${i}`}>
              <div className="font-medium text-slate-900 whitespace-pre-line"><span className="text-slate-400 mr-1">{i + 1}.</span>{q.statement}</div>
              {q.type === "multipla" && <p className="text-xs text-slate-400 mt-1">Marque todas as corretas.</p>}
              <div className="mt-3 space-y-2">
                {q.options.map((o, j) => {
                  const on = (answers[q.id] || []).includes(o.id);
                  return (
                    <label key={o.id} className={`flex items-center gap-3 rounded-xl border-[1.5px] px-3.5 py-3 cursor-pointer transition-colors ${on ? "border-[#1E9E2A] bg-green-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
                      {q.type === "multipla"
                        ? <Checkbox checked={on} onCheckedChange={() => pick(q, o.id)} className="sr-only" />
                        : <input type="radio" name={q.id} checked={on} onChange={() => pick(q, o.id)} className="sr-only" />}
                      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold ${on ? "bg-[#1E9E2A] text-white" : "bg-slate-100 text-slate-500"}`}>
                        {on ? <CheckCircle2 className="h-4 w-4" /> : String.fromCharCode(65 + j)}
                      </span>
                      <span className={`text-[15px] ${on ? "text-slate-900 font-medium" : "text-slate-700"}`}>{o.text}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex justify-end">
            <Button onClick={() => submit(false)} disabled={busy} className="h-12 text-base text-white px-10 w-full sm:w-auto" style={{ background: "#1E9E2A" }} data-testid="exam-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Enviar prova</>}
            </Button>
          </div>
        </>
      )}
    </main>
  );
}
